import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Search, Trash2, Plus, X } from "lucide-react";
import type { MaintainerUser } from "@/lib/db/maintainers";

interface MaintainerManagementProps {
  onClose?: () => void;
}

export function MaintainerManagement({ onClose }: MaintainerManagementProps) {
  const [users, setUsers] = useState<MaintainerUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDiscordId, setNewDiscordId] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/admin/maintainers", {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to load maintainers");
      }
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load maintainers");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDiscordId.trim()) {
      alert("Discord ID is required");
      return;
    }

    setAdding(true);
    setError(null);
    try {
      const response = await fetch("/api/admin/maintainers", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          discord_id: newDiscordId.trim(),
          email: newEmail.trim() || undefined,
          name: newName.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || "Failed to add maintainer");
      }

      // Reset form
      setNewDiscordId("");
      setNewEmail("");
      setNewName("");
      setShowAddForm(false);
      
      // Reload users
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add maintainer");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (discordId: string) => {
    if (!confirm(`Are you sure you want to remove maintainer ${discordId}? This action cannot be undone.`)) {
      return;
    }

    setDeletingId(discordId);
    setError(null);
    try {
      const response = await fetch(`/api/admin/maintainers/${encodeURIComponent(discordId)}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || "Failed to remove maintainer");
      }

      setUsers(prev => prev.filter(user => user.discord_id !== discordId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove maintainer");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.discord_id.toLowerCase().includes(query) ||
      (user.email && user.email.toLowerCase().includes(query)) ||
      (user.name && user.name.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Maintainer Management</CardTitle>
            <CardDescription>
              Manage users who can create and edit division analyses
            </CardDescription>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by Discord ID, email, or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            variant={showAddForm ? "outline" : "default"}
          >
            {showAddForm ? (
              <>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Add Maintainer
              </>
            )}
          </Button>
        </div>

        {showAddForm && (
          <Card className="bg-muted/50">
            <CardHeader>
              <CardTitle className="text-lg">Add New Maintainer</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="maintainer-discord-id" className="text-sm font-medium">
                    Discord ID <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="maintainer-discord-id"
                    value={newDiscordId}
                    onChange={(e) => setNewDiscordId(e.target.value)}
                    placeholder="123456789012345678"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="maintainer-email" className="text-sm font-medium">
                    Email (optional)
                  </label>
                  <Input
                    id="maintainer-email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="maintainer-name" className="text-sm font-medium">
                    Name (optional)
                  </label>
                  <Input
                    id="maintainer-name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Display Name"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={adding}>
                    {adding ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Maintainer
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddForm(false);
                      setNewDiscordId("");
                      setNewEmail("");
                      setNewName("");
                    }}
                    disabled={adding}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Discord ID</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    {searchQuery ? "No maintainers found matching your search" : "No maintainers found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.discord_id}>
                    <TableCell className="font-mono text-xs">{user.discord_id}</TableCell>
                    <TableCell>{user.email || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>{user.name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(user.discord_id)}
                        disabled={deletingId === user.discord_id}
                        className="text-destructive hover:text-destructive"
                      >
                        {deletingId === user.discord_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
